/**
 * HF rich text — plain-text storage with BMP Private Use Area delimiters (U+E000–U+E005).
 * PUA chars never appear in normal user typing and are ignored by markdown parsers.
 *
 * Bold:   \uE000 … \uE001
 * Italic: \uE002 … \uE003
 * Underline: \uE004 … \uE005
 * Paragraphs: blank line (\n\n); line breaks within a paragraph: \n
 */

export type HfRichTextFormat = 'bold' | 'italic' | 'underline';

export const HF_RICH_TEXT_MARKERS = {
  bold: { open: '\uE000', close: '\uE001', tag: 'b' as const },
  italic: { open: '\uE002', close: '\uE003', tag: 'i' as const },
  underline: { open: '\uE004', close: '\uE005', tag: 'u' as const },
} as const;

export const DEFAULT_HF_RICH_TEXT_FORMATS: HfRichTextFormat[] = ['bold', 'italic', 'underline'];

const HTML_TAG_RE = /<\/?([a-z0-9]+)([^>]*)>/gi;
const LOOKS_LIKE_HTML_RE = /<\/?[a-z][\s\S]*>/i;

const FORMAT_BY_OPEN: Record<string, HfRichTextFormat> = {
  [HF_RICH_TEXT_MARKERS.bold.open]: 'bold',
  [HF_RICH_TEXT_MARKERS.italic.open]: 'italic',
  [HF_RICH_TEXT_MARKERS.underline.open]: 'underline',
};

const FORMAT_BY_CLOSE: Record<string, HfRichTextFormat> = {
  [HF_RICH_TEXT_MARKERS.bold.close]: 'bold',
  [HF_RICH_TEXT_MARKERS.italic.close]: 'italic',
  [HF_RICH_TEXT_MARKERS.underline.close]: 'underline',
};

const FORMAT_BY_HTML_TAG: Record<string, HfRichTextFormat> = {
  b: 'bold',
  strong: 'bold',
  i: 'italic',
  em: 'italic',
  u: 'underline',
};

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function decodeBasicEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

function allowedHtmlTags(formats: readonly HfRichTextFormat[]): Set<string> {
  const tags = new Set(['P', 'BR', 'DIV']);
  if (formats.includes('bold')) {
    tags.add('B');
    tags.add('STRONG');
  }
  if (formats.includes('italic')) {
    tags.add('I');
    tags.add('EM');
  }
  if (formats.includes('underline')) tags.add('U');
  return tags;
}

/** True when the string looks like HTML rather than HF markup. */
export function looksLikeHtml(value: string): boolean {
  return LOOKS_LIKE_HTML_RE.test(value);
}

/** Strip disallowed tags; keep only inline/block tags needed for WYSIWYG round-trip. */
export function sanitizeRichTextHtml(html: string, formats: readonly HfRichTextFormat[] = DEFAULT_HF_RICH_TEXT_FORMATS): string {
  const trimmed = html.trim();
  if (!trimmed) return '';

  const allowed = allowedHtmlTags(formats);
  return trimmed
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(HTML_TAG_RE, (match, tag: string) => {
      const upper = tag.toUpperCase();
      if (allowed.has(upper)) return match;
      return '';
    });
}

function findMatchingClose(text: string, start: number, format: HfRichTextFormat): number {
  const { open, close } = HF_RICH_TEXT_MARKERS[format];
  let depth = 1;
  let i = start;
  while (i < text.length && depth > 0) {
    if (text.startsWith(open, i)) {
      depth += 1;
      i += open.length;
      continue;
    }
    if (text.startsWith(close, i)) {
      depth -= 1;
      if (depth === 0) return i;
      i += close.length;
      continue;
    }
    i += 1;
  }
  return -1;
}

function nextMarkupIndex(text: string, from: number): number {
  let next = text.length;
  for (const format of DEFAULT_HF_RICH_TEXT_FORMATS) {
    for (const marker of [HF_RICH_TEXT_MARKERS[format].open, HF_RICH_TEXT_MARKERS[format].close] as const) {
      const at = text.indexOf(marker, from);
      if (at !== -1 && at < next) next = at;
    }
  }
  return next;
}

function inlineHfToHtml(text: string, formats: readonly HfRichTextFormat[]): string {
  let out = '';
  let i = 0;
  while (i < text.length) {
    const openFormat = FORMAT_BY_OPEN[text[i] ?? ''];
    if (openFormat && formats.includes(openFormat)) {
      const closeIdx = findMatchingClose(text, i + 1, openFormat);
      if (closeIdx !== -1) {
        const inner = inlineHfToHtml(text.slice(i + 1, closeIdx), formats);
        out += `<${HF_RICH_TEXT_MARKERS[openFormat].tag}>${inner}</${HF_RICH_TEXT_MARKERS[openFormat].tag}>`;
        i = closeIdx + 1;
        continue;
      }
    }
    const next = nextMarkupIndex(text, i + 1);
    out += escapeHtml(text.slice(i, next));
    i = next;
  }
  return out;
}

/** Render HF markup to safe HTML for contentEditable / display. */
export function hfRichTextToHtml(markup: string, formats: readonly HfRichTextFormat[] = DEFAULT_HF_RICH_TEXT_FORMATS): string {
  const trimmed = sanitizeHfRichText(markup);
  if (!trimmed) return '';

  const paragraphs = trimmed.split(/\n{2,}/);
  return paragraphs
    .map((para) => {
      const withBreaks = para
        .split('\n')
        .map((line) => inlineHfToHtml(line, formats))
        .join('<br>');
      return `<p>${withBreaks}</p>`;
    })
    .join('');
}

function htmlFragmentToHf(html: string, formats: readonly HfRichTextFormat[]): string {
  let result = '';
  let i = 0;
  while (i < html.length) {
    const tagMatch = /^<\/?([a-z0-9]+)[^>]*>/i.exec(html.slice(i));
    if (tagMatch) {
      const full = tagMatch[0];
      const tag = tagMatch[1]?.toLowerCase() ?? '';
      const format = FORMAT_BY_HTML_TAG[tag];
      if (format && formats.includes(format)) {
        result += full.startsWith('</') ? HF_RICH_TEXT_MARKERS[format].close : HF_RICH_TEXT_MARKERS[format].open;
      }
      i += full.length;
      continue;
    }
    const nextTag = html.indexOf('<', i);
    const textEnd = nextTag === -1 ? html.length : nextTag;
    result += decodeBasicEntities(html.slice(i, textEnd));
    i = textEnd;
  }
  return result;
}

/** Convert sanitized HTML subset → HF markup. Never emits HTML. */
export function htmlToHfRichText(html: string, formats: readonly HfRichTextFormat[] = DEFAULT_HF_RICH_TEXT_FORMATS): string {
  const safe = sanitizeRichTextHtml(html, formats);
  if (!safe) return '';

  let hf = safe
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/div>\s*<div[^>]*>/gi, '\n\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    .replace(/<\/?div[^>]*>/gi, '')
    .replace(/<\/?p[^>]*>/gi, '');

  hf = htmlFragmentToHf(hf, formats);
  hf = hf.replace(/<[^>]+>/g, '');
  return hf.replace(/\u00a0/g, ' ').trim();
}

/** Strip every HTML tag — stored rich text is HF markup only. */
export function sanitizeHfRichText(markup: string): string {
  const trimmed = markup.trim();
  if (!trimmed) return '';

  return trimmed
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, '')
    .trim();
}

/** Normalize any stored/input value to HF markup (migrates legacy HTML; never keeps HTML). */
export function normalizeHfRichText(value: string, formats: readonly HfRichTextFormat[] = DEFAULT_HF_RICH_TEXT_FORMATS): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (looksLikeHtml(trimmed)) return htmlToHfRichText(trimmed, formats);
  return sanitizeHfRichText(trimmed);
}

/** Remove markup delimiters for formats that are not enabled (unwrap, keep text). */
export function stripHfRichTextFormats(markup: string, formats: readonly HfRichTextFormat[]): string {
  let out = '';
  for (let i = 0; i < markup.length; i += 1) {
    const ch = markup[i] ?? '';
    const openFormat = FORMAT_BY_OPEN[ch];
    const closeFormat = FORMAT_BY_CLOSE[ch];
    if ((openFormat && !formats.includes(openFormat)) || (closeFormat && !formats.includes(closeFormat))) {
      continue;
    }
    out += ch;
  }
  return out;
}

/** Visible character count — strips PUA format markers, keeps newlines and spaces. */
export function hfRichTextPlainLength(markup: string): number {
  let count = 0;
  for (let i = 0; i < markup.length; i += 1) {
    const ch = markup[i] ?? '';
    if (FORMAT_BY_OPEN[ch] || FORMAT_BY_CLOSE[ch]) continue;
    count += 1;
  }
  return count;
}
